import React from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import type { ServiceSpec } from "src/domains/application/types";

import {
  type FormService,
  type ServiceType,
  createEmptyService,
  serviceSpecToFormService,
} from "./manifest-form-helpers";

// ── Constants ────────────────────────────────────────────────────────────────

const SERVICE_TYPES: { value: ServiceType; label: string }[] = [
  { value: "server", label: "Server" },
  { value: "static", label: "Static" },
  { value: "worker", label: "Worker" },
  { value: "stateful", label: "Stateful" },
  { value: "scheduler", label: "Scheduler" },
  { value: "cron", label: "Cron" },
];

// ── Types ────────────────────────────────────────────────────────────────────

interface ManifestFormEditorProps {
  services: FormService[];
  pipelineMode: string;
  templateServices?: ServiceSpec[];
  onChange: (services: FormService[]) => void;
  onPipelineModeChange: (mode: string) => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export function ManifestFormEditor({
  services,
  pipelineMode,
  templateServices,
  onChange,
  onPipelineModeChange,
}: ManifestFormEditorProps): React.ReactElement {
  const [addingService, setAddingService] = React.useState(false);

  const handleAddService = (svc: FormService) => {
    onChange([...services, svc]);
    setAddingService(false);
  };

  const handleRemoveService = (index: number) => {
    onChange(services.filter((_, i) => i !== index));
  };

  const handleServiceChange = (
    index: number,
    updates: Partial<FormService>,
  ) => {
    onChange(services.map((s, i) => (i === index ? { ...s, ...updates } : s)));
  };

  // Available template services not yet in the manifest
  const availableTemplateServices = (templateServices ?? []).filter(
    (ts) => !services.some((s) => s.name === ts.name),
  );

  return (
    <Box sx={{ p: 2.5 }}>
      {/* Pipeline Mode */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          Pipeline Mode
        </Typography>
        <TextField
          select
          size="small"
          value={pipelineMode}
          onChange={(e) => onPipelineModeChange(e.target.value)}
          sx={{ minWidth: 220 }}
          SelectProps={{ native: true }}
        >
          <option value="rusha_managed">Rusha Managed</option>
          <option value="ci_build_rusha_deploy">CI Build + Rusha Deploy</option>
        </TextField>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Services header */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Services
          </Typography>
          <Chip label={services.length} size="small" variant="outlined" />
        </Stack>
      </Stack>

      {/* Service cards */}
      <Stack spacing={1.5}>
        {services.map((service, index) => (
          <ServiceCard
            key={index}
            service={service}
            index={index}
            onChange={(updates) => handleServiceChange(index, updates)}
            onRemove={() => handleRemoveService(index)}
          />
        ))}

        {/* Add service section */}
        {addingService ? (
          <AddServiceSection
            availableTemplateServices={availableTemplateServices}
            onAdd={handleAddService}
            onCancel={() => setAddingService(false)}
          />
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddingService(true)}
            sx={{
              textTransform: "none",
              fontSize: 12,
              fontWeight: 600,
              alignSelf: "flex-start",
            }}
          >
            Add Service
          </Button>
        )}

        {services.length === 0 && !addingService && (
          <Alert severity="info">
            No services defined. Click &quot;Add Service&quot; to create one.
          </Alert>
        )}
      </Stack>
    </Box>
  );
}

// ── Service Card ──────────────────────────────────────────────────────────────

interface ServiceCardProps {
  service: FormService;
  index: number;
  onChange: (updates: Partial<FormService>) => void;
  onRemove: () => void;
}

function ServiceCard({
  service,
  index,
  onChange,
  onRemove,
}: ServiceCardProps): React.ReactElement {
  const [expanded, setExpanded] = React.useState(index === 0);
  const showBuildFields =
    service.type === "server" ||
    service.type === "static" ||
    service.type === "worker";
  const showImageField = service.type === "stateful";
  const showStorageField =
    service.type === "stateful" || service.type === "scheduler";

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      variant="outlined"
      sx={{
        "&:before": { display: "none" },
        bgcolor: "rgba(10,20,36,0.2)",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ width: "100%", pr: 1 }}
        >
          <Chip
            label={service.type}
            size="small"
            variant="outlined"
            sx={{ fontSize: 11 }}
          />
          <Typography sx={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
            {service.name || `Service ${index + 1}`}
          </Typography>
          {service.port !== undefined && (
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
              :{service.port}
            </Typography>
          )}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            sx={{ color: "error.main" }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 1 }}>
        <Stack spacing={2}>
          {/* Name + Type */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Name"
              size="small"
              value={service.name}
              onChange={(e) => onChange({ name: e.target.value })}
              sx={{ flex: 1 }}
              required
            />
            <TextField
              label="Type"
              size="small"
              value={service.type}
              onChange={(e) =>
                onChange({ type: e.target.value as ServiceType })
              }
              select
              SelectProps={{ native: true }}
              sx={{ minWidth: 140 }}
            >
              {SERVICE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </TextField>
          </Stack>

          {/* Port + Internet-facing */}
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Port"
              size="small"
              type="number"
              value={service.port ?? ""}
              onChange={(e) =>
                onChange({
                  port: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              sx={{ width: 120 }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                size="small"
                checked={service.internetFacing}
                onChange={(e) => onChange({ internetFacing: e.target.checked })}
              />
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Internet-facing
              </Typography>
            </Stack>
          </Stack>

          {/* Build fields (server/static/worker) */}
          {showBuildFields && (
            <Stack direction="row" spacing={2}>
              <TextField
                label="Dockerfile"
                size="small"
                value={service.dockerfile ?? ""}
                onChange={(e) =>
                  onChange({ dockerfile: e.target.value || undefined })
                }
                placeholder="src/apps/api/Dockerfile"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Context"
                size="small"
                value={service.context ?? ""}
                onChange={(e) =>
                  onChange({ context: e.target.value || undefined })
                }
                placeholder=". (repo root)"
                sx={{ width: 160 }}
              />
            </Stack>
          )}

          {/* Image field (stateful) */}
          {showImageField && (
            <TextField
              label="Image"
              size="small"
              value={service.image ?? ""}
              onChange={(e) => onChange({ image: e.target.value || undefined })}
              placeholder="postgres:16-alpine"
              sx={{ flex: 1 }}
            />
          )}

          {/* Resources */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="CPU"
              size="small"
              value={service.cpu ?? ""}
              onChange={(e) => onChange({ cpu: e.target.value || undefined })}
              placeholder="500m"
              sx={{ width: 120 }}
            />
            <TextField
              label="Memory"
              size="small"
              value={service.memory ?? ""}
              onChange={(e) =>
                onChange({ memory: e.target.value || undefined })
              }
              placeholder="512Mi"
              sx={{ width: 120 }}
            />
            {showStorageField && (
              <TextField
                label="Storage"
                size="small"
                value={service.storage ?? ""}
                onChange={(e) =>
                  onChange({ storage: e.target.value || undefined })
                }
                placeholder="20Gi"
                sx={{ width: 120 }}
              />
            )}
          </Stack>

          {/* Env vars */}
          <EnvVarEditor
            env={service.env}
            onChange={(env) => onChange({ env })}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// ── Env Var Editor ────────────────────────────────────────────────────────────

interface EnvVarEditorProps {
  env: FormService["env"];
  onChange: (env: FormService["env"]) => void;
}

function EnvVarEditor({
  env,
  onChange,
}: EnvVarEditorProps): React.ReactElement {
  const handleAdd = () => {
    onChange([...env, { name: "", value: "" }]);
  };

  const handleRemove = (index: number) => {
    onChange(env.filter((_, i) => i !== index));
  };

  const handleChange = (
    index: number,
    field: "name" | "value",
    value: string,
  ) => {
    onChange(env.map((e, i) => (i === index ? { ...e, [field]: value } : e)));
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: "text.secondary",
            textTransform: "uppercase",
          }}
        >
          Environment Variables
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={handleAdd}
          sx={{ textTransform: "none", fontSize: 11 }}
        >
          Add Var
        </Button>
      </Stack>
      {env.map((v, i) => (
        <Stack key={i} direction="row" spacing={1} sx={{ mb: 0.5 }}>
          <TextField
            size="small"
            placeholder="NAME"
            value={v.name}
            onChange={(e) => handleChange(i, "name", e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            placeholder="value"
            value={v.value}
            onChange={(e) => handleChange(i, "value", e.target.value)}
            sx={{ flex: 1 }}
          />
          <IconButton
            size="small"
            onClick={() => handleRemove(i)}
            sx={{ color: "error.main" }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      {env.length === 0 && (
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          No env vars configured.
        </Typography>
      )}
    </Box>
  );
}

// ── Add Service Section ───────────────────────────────────────────────────────

interface AddServiceSectionProps {
  availableTemplateServices: ServiceSpec[];
  onAdd: (service: FormService) => void;
  onCancel: () => void;
}

function AddServiceSection({
  availableTemplateServices,
  onAdd,
  onCancel,
}: AddServiceSectionProps): React.ReactElement {
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<ServiceSpec | null>(null);
  const [customMode, setCustomMode] = React.useState(false);

  if (availableTemplateServices.length > 0 && !customMode) {
    return (
      <Box
        sx={{
          p: 2,
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1 }}>
          Add a service
        </Typography>
        <Autocomplete
          size="small"
          options={availableTemplateServices}
          getOptionLabel={(opt) => `${opt.name} (${opt.type})`}
          value={selectedTemplate}
          onChange={(_, value) => {
            setSelectedTemplate(value);
            if (value) {
              onAdd(serviceSpecToFormService(value));
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select from template"
              placeholder="Choose a service to add"
            />
          )}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            onClick={() => setCustomMode(true)}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            Or add custom service
          </Button>
          <Button
            size="small"
            onClick={onCancel}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            Cancel
          </Button>
        </Stack>
      </Box>
    );
  }

  // Custom service or no template services
  return (
    <Box
      sx={{
        p: 2,
        border: "1px dashed",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1 }}>
        Add custom service
      </Typography>
      <CustomServiceForm
        onAdd={(svc) => {
          onAdd(svc);
          setCustomMode(false);
        }}
        onCancel={() => {
          setCustomMode(false);
          onCancel();
        }}
      />
    </Box>
  );
}

// ── Custom Service Form ───────────────────────────────────────────────────────

interface CustomServiceFormProps {
  onAdd: (service: FormService) => void;
  onCancel: () => void;
}

function CustomServiceForm({
  onAdd,
  onCancel,
}: CustomServiceFormProps): React.ReactElement {
  const [svc, setSvc] = React.useState<FormService>(createEmptyService());

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <TextField
          label="Name"
          size="small"
          value={svc.name}
          onChange={(e) => setSvc({ ...svc, name: e.target.value })}
          sx={{ flex: 1 }}
          required
        />
        <TextField
          label="Type"
          size="small"
          value={svc.type}
          onChange={(e) =>
            setSvc({ ...svc, type: e.target.value as ServiceType })
          }
          select
          SelectProps={{ native: true }}
          sx={{ minWidth: 140 }}
        >
          {SERVICE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </TextField>
      </Stack>
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="contained"
          disabled={!svc.name}
          onClick={() => onAdd(svc)}
          sx={{ textTransform: "none", fontSize: 12 }}
        >
          Add
        </Button>
        <Button
          size="small"
          onClick={onCancel}
          sx={{ textTransform: "none", fontSize: 12 }}
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
}
